import React, { useContext, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { RiFileExcel2Line } from "react-icons/ri";
import { FaSyncAlt } from "react-icons/fa";
import * as XLSX from "xlsx";
import { useUser } from "../../../context/UserContext.tsx";
import AgentContext from "../../../context/admin/Agent.tsx";
import {
    CLOSING_LINE_CACHE_STALE_MS,
    CLOSING_LINE_CACHE_TTL_MS,
    closingLineAgentCacheKey,
    getClosingLineAnalysis,
} from "../../../services/admin/BetsService.tsx";
import { useCachedQuery } from "../../../helpers/queryCache.tsx";
import { formatCurrency } from "../../../helpers/functions.tsx";
import {
    formatAdminFiguresDateLabel,
    formatAdminFiguresTime,
} from "../../../helpers/adminFiguresTime.tsx";
import {
    closingLinePercentageLabel,
    closingLinePointsLabel,
    closingLinePriceLabel,
    signedClassName,
    summarizeClosingLineRows,
    toClosingLinePlayerRow,
} from "../../../helpers/closingLineAnalysis.ts";
import { logUserActivity } from "../../../services/player/LoginService.tsx";
import { buildVisitedUserNavState } from "../../../helpers/adminVisitedUserCache.tsx";
import "../../../styles/admin/ClosingLineAnalysis.css";

const formatWinLoss = (value: number) => `$${formatCurrency(value, 0)}`;
const SKELETON_ROWS = 8;

const ClosingLineTableSkeleton = () => (
    <>
        {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <tr key={index} aria-hidden="true">
                {Array.from({ length: 8 }).map((__, cell) => (
                    <td key={cell}>
                        <span className="rect-placeholder closing-line-skeleton-cell" />
                    </td>
                ))}
            </tr>
        ))}
    </>
);

const ClosingLineAnalysis = () => {
    const { user: currentUser, userIpData, isLoading: isUserLoading } = useUser();
    const { allPlayers } = useContext(AgentContext);
    const [showHelp, setShowHelp] = useState(false);

    const { data: docs, isLoading } = useCachedQuery(
        currentUser?.userName ? closingLineAgentCacheKey(currentUser.userName) : "",
        () => getClosingLineAnalysis(currentUser!),
        [currentUser?.userName],
        { ttlMs: CLOSING_LINE_CACHE_TTL_MS, staleMs: CLOSING_LINE_CACHE_STALE_MS },
    );

    const rows = useMemo(
        () => (docs ?? []).map(toClosingLinePlayerRow),
        [docs],
    );
    const totals = useMemo(() => summarizeClosingLineRows(rows), [rows]);
    const showSkeleton = isLoading && rows.length === 0;

    const exportToExcel = () => {
        const sheetRows = rows.map((row) => ({
            Customer: row.userName,
            Points: closingLinePointsLabel(row),
            Price: closingLinePriceLabel(row),
            "Beat Line": row.beatLine,
            "Total Bets": row.totalBets,
            Percentage: closingLinePercentageLabel(row),
            "Win/Loss": formatWinLoss(row.winLoss),
        }));

        const worksheet = XLSX.utils.json_to_sheet(sheetRows);
        worksheet["!cols"] = [
            { wpx: 110 },
            { wpx: 90 },
            { wpx: 80 },
            { wpx: 90 },
            { wpx: 90 },
            { wpx: 100 },
            { wpx: 100 },
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Closing line analysis");

        const today = new Date();
        const formattedDate = formatAdminFiguresDateLabel(today, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
        const formattedTime = formatAdminFiguresTime(today, {
            hour: "numeric",
            minute: "2-digit",
        }).replace(":", "_").replace(" ", "");
        XLSX.writeFile(workbook, `ClosingLineAnalysis_${formattedDate.replace(",", "")} ${formattedTime}.xlsx`);
    };

    if (!isUserLoading && currentUser?.userType !== "masterAgent") {
        return <Navigate to="/homeAdmin" replace />;
    }

    return (
        <div className="container-fluid closing-line-page">
            <div className="mt-2 container-body">
                <div className="closing-line-header">
                    <div className="closing-line-title-wrap">
                        <div className="closing-line-title-row">
                            <h1 className="closing-line-title">Closing line analysis</h1>
                            <RiFileExcel2Line className="excel-icon closing-line-excel" onClick={exportToExcel} title="Export to Excel" />
                            {isLoading && rows.length > 0 && (
                                <FaSyncAlt className="closing-line-inline-loader" aria-label="Refreshing" />
                            )}
                        </div>
                        <div className="closing-line-subtitle">
                            Based on last 14 days activity
                            <button type="button" className="closing-line-help-link" onClick={() => setShowHelp((open) => !open)}>
                                What is this?
                            </button>
                        </div>
                    </div>
                </div>

                {showHelp && (
                    <div className="closing-line-help">
                        <p>
                            Closing Line Analysis measures each player’s bets against the closing line (closing-line value)
                            to help you spot sharp/advantage players. The report is based on the last 14 days of activity,
                            and an Excel export is available.
                        </p>
                        <ul>
                            <li><strong>Customer</strong> — the account ID.</li>
                            <li><strong>Points</strong> — the average points by which the player beat or missed the closing line (spread/total).</li>
                            <li><strong>Price</strong> — the average price difference versus the closing line (moneyline, in cents).</li>
                            <li><strong>Beat Line</strong> — the number of bets that beat the closing line.</li>
                            <li><strong>Total Bets</strong> — the total number of bets analyzed.</li>
                            <li><strong>Percentage</strong> — the share of bets that beat the closing line.</li>
                            <li><strong>Win/Loss</strong> — the player’s win/loss over the analyzed period.</li>
                            <li><strong>Full Analysis</strong> — a View link that drills into that player’s detailed analysis.</li>
                        </ul>
                        <div className="closing-line-help-units">
                            <p><strong>Two units are used on purpose:</strong></p>
                            <p>Moneyline: <strong>+50¢</strong> of line value. Example: took -150, closed -200 → beat line by 50¢.</p>
                            <p>Spread/total: <strong>+2.0 pts</strong>. Example: took -2.5, closed -0.5 → beat line by 2.0 pts.</p>
                        </div>
                    </div>
                )}

                <div className="closing-line-table-wrap table-responsive">
                    {!showSkeleton && rows.length === 0 ? (
                        <div className="closing-line-empty">No betting activity in the last 14 days.</div>
                    ) : (
                        <table className="closing-line-table">
                            <thead>
                                <tr>
                                    <th>Customer</th>
                                    <th>Points</th>
                                    <th>Price</th>
                                    <th>Beat Line</th>
                                    <th>Total Bets</th>
                                    <th>Percentage</th>
                                    <th>Win/Loss</th>
                                    <th>Full Analysis</th>
                                </tr>
                            </thead>
                            <tbody>
                                {showSkeleton ? (
                                    <ClosingLineTableSkeleton />
                                ) : (
                                    rows.map((row) => {
                                        const player = allPlayers.find((item) => item.userName === row.userName);
                                        return (
                                            <tr key={row.userName}>
                                                <td className="col-customer">
                                                    <Link
                                                        to={`/user/${row.userName}`}
                                                        state={player ? buildVisitedUserNavState(player, { from: "Analytics" }) : undefined}
                                                        onClick={() => logUserActivity(currentUser!.userName, `View User Basic: ${row.userName}`, userIpData)}
                                                    >
                                                        {row.userName}
                                                    </Link>
                                                </td>
                                                <td className={signedClassName(row.points)}>{closingLinePointsLabel(row)}</td>
                                                <td className={signedClassName(row.price)}>{closingLinePriceLabel(row)}</td>
                                                <td>{row.beatLine}</td>
                                                <td>{row.totalBets}</td>
                                                <td>{closingLinePercentageLabel(row)}</td>
                                                <td className={signedClassName(row.winLoss)}>{formatWinLoss(row.winLoss)}</td>
                                                <td>
                                                    <Link
                                                        to={`/user/${row.userName}/closingLine`}
                                                        state={player ? buildVisitedUserNavState(player, { from: "Analytics" }) : undefined}
                                                        onClick={() => {
                                                            logUserActivity(currentUser!.userName, `View Closing Line Analysis: ${row.userName}`, userIpData);
                                                        }}
                                                    >
                                                        View
                                                    </Link>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                            {!showSkeleton && (
                                <tfoot>
                                    <tr>
                                        <td className="col-customer">Total</td>
                                        <td className={signedClassName(totals.points)}>{closingLinePointsLabel(totals)}</td>
                                        <td className={signedClassName(totals.price)}>{closingLinePriceLabel(totals)}</td>
                                        <td>{totals.beatLine}</td>
                                        <td>{totals.totalBets}</td>
                                        <td>{closingLinePercentageLabel(totals)}</td>
                                        <td className={signedClassName(totals.winLoss)}>{formatWinLoss(totals.winLoss)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClosingLineAnalysis;
